import { AsyncPipe, NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, inject, OnInit, Renderer2, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, RouterOutlet } from '@angular/router';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { debounceTime, filter, fromEvent } from 'rxjs';

import { AuthService } from '@bn/auth/services';
import { AbstractLazyDirective } from '@bn/features/abstracts/abstract-lazy.directive';
import { AppRoutes, BillingType } from '@bn/primitives';
import { AppStore } from '@bn/product-stores/app';
import { UserDetails } from '@bn/shared/core/api';
import { ContentService, VersionCheckService } from '@bn/shared/core/services';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { DroppableDirective } from '@bn/web-ui/drop-files/droppable.directive';
import { TopbarComponent } from '@bn/web-ui/topbar/topbar.component';

import { CentralWidgetComponent } from '../central-widget/central-widget.component';
import { MobileHeaderComponent } from '../mobile-header/mobile-header.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { SurveyWidgetComponent } from '../survey-widget/survey-widget.component';
import { UpdateNotificationComponent } from '../update-notification/update-notification.component';

@Component({
  selector: 'bn-dashboard-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [
    TopbarComponent,
    DroppableDirective,
    SidebarComponent,
    FaIconComponent,
    ButtonComponent,
    NgClass,
    RouterOutlet,
    CentralWidgetComponent,
    UpdateNotificationComponent,
    SurveyWidgetComponent,
    MobileHeaderComponent,
    AsyncPipe,
  ],
})
export class DashboardLayoutComponent extends AbstractLazyDirective implements OnInit, AfterViewInit {
  readonly #appStore = inject(AppStore);
  readonly #authService = inject(AuthService);
  private readonly renderer2 = inject(Renderer2);
  private readonly bnSidebar = viewChild('bnSidebar', { read: ElementRef });

  readonly contentService = inject(ContentService);
  readonly versionCheckService = inject(VersionCheckService);
  readonly blurThumbnails = this.#appStore.blurThumbnails;
  readonly authData = this.#authService.getAuthStoreSignal();
  readonly userDetails = toSignal<UserDetails | null>(this.userDetailsService.getUserDetails());

  isMobileMenuOpened = false;

  #lastViewportWidth = window.innerWidth;
  #isContentAsideShown = false;

  constructor() {
    super();

    this.contentService.getContentData().subscribe(({ showSidebar }) => {
      // ignore undefined, must be explicitly NULL
      if (showSidebar === undefined) return;

      const isShown = showSidebar === null ? undefined : showSidebar;

      this.#isContentAsideShown = !!isShown;

      if (isShown) {
        this.renderer2.addClass(document.body, 'overflow-hidden');
      } else if (!this.isMobileMenuOpened) {
        // The nav drawer holds its own lock — don't release it on the aside's behalf.
        this.renderer2.removeClass(document.body, 'overflow-hidden');
      }
    });
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart)).subscribe(() => {
        if (this.isMobileMenuOpened) this.onToggleMenu(false);
      }),
    );
  }

  onToggleMenu(isOpened: boolean): void {
    this.isMobileMenuOpened = isOpened;

    // Lock body scroll while the mobile navigation drawer is open.
    if (isOpened) {
      this.renderer2.addClass(document.body, 'overflow-hidden');
    } else if (!this.#isContentAsideShown) {
      // The content aside holds its own lock — don't release it on the drawer's behalf.
      this.renderer2.removeClass(document.body, 'overflow-hidden');
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (this.isMobileMenuOpened) this.onToggleMenu(false);
  }

  public override ngOnDestroy(): void {
    // Never leave the body scroll-locked if the layout is destroyed with the drawer open.
    this.renderer2.removeClass(document.body, 'overflow-hidden');
    super.ngOnDestroy();
  }

  public logout(): void {
    this.#authService.logout();
  }

  public onToggleBlurThumbnails(hide: boolean): void {
    this.#appStore.setLocalStorage({
      key: 'blurThumbnails',
      value: hide ?? false,
    });
  }

  public shouldShowNegativeBalanceBanner(): boolean {
    const userDetails = this.userDetails();

    if (!userDetails) return false;

    // Don't show banner if account is already disabled (modal will show instead)
    if (userDetails.AccountDisabled) return false;

    // Don't show for trial or postpaid users
    if (userDetails.BillingType === BillingType.FreeTrial || userDetails.BillingType === BillingType.PostPaidPayAsYouGo) {
      return false;
    }

    // Show only for prepaid users with negative balance
    return userDetails.Balance < 0;
  }

  public topUpAccount(): void {
    this.router.navigate([`/${AppRoutes.Account}/${AppRoutes.Billing}`]);
  }

  /**
   * Set css variables now and on every window.resize event for:
   *
   * --bn-sidebar-width
   * --bn-sidebar-padding-bottom: adjust so menu can be fully visible when scrolled down on small displays
   */
  public ngAfterViewInit() {
    const bnSidebar = this.bnSidebar();
    const bnSidebarItem = bnSidebar?.nativeElement?.querySelector('aside');
    const bnSidebarBottom = bnSidebar?.nativeElement?.querySelector('.bn-sidebar__bottom');

    this.contentService.setRootElementStyleProperty('--bn-sidebar-width', `${bnSidebarItem?.offsetWidth ?? 0}px`);
    this.contentService.setRootElementStyleProperty('--bn-sidebar-padding-bottom', `${bnSidebarBottom?.offsetHeight ?? 0}px`);

    this.subscriptions.add(
      fromEvent(window, 'resize')
        .pipe(debounceTime(200))
        .subscribe(() => {
          this.contentService.setRootElementStyleProperty('--bn-sidebar-width', `${bnSidebarItem?.offsetWidth ?? 0}px`);
          this.contentService.setRootElementStyleProperty('--bn-sidebar-padding-bottom', `${bnSidebarBottom?.offsetHeight ?? 0}px`);

          // Close the drawer only when the viewport width actually changed — the on-screen
          // keyboard on mobile fires resize (height only) and must not close the menu.
          const currentWidth = window.innerWidth;

          if (this.isMobileMenuOpened && currentWidth !== this.#lastViewportWidth) this.onToggleMenu(false);

          this.#lastViewportWidth = currentWidth;
        }),
    );
  }
}
